// src/i18n/LanguageProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from "react";
import i18n from "i18next";

export type SupportedLang = "en" | "hi" | "fr" | "es";

type Ctx = {
  lang: SupportedLang;
  setLang: (l: SupportedLang) => void;
};

const SUPPORTED: SupportedLang[] = ["en", "hi", "fr", "es"];

function normalizeLang(lng?: string | null): SupportedLang {
  if (!lng) return "en";
  const base = lng.split("-")[0]; // "en-US" -> "en"
  if (SUPPORTED.includes(base as SupportedLang)) {
    return base as SupportedLang;
  }
  return "en";
}

const LanguageCtx = createContext<Ctx>({
  lang: "en",
  setLang: () => {}
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<SupportedLang>(() =>
    normalizeLang(i18n.resolvedLanguage || i18n.language)
  );

  useEffect(() => {
    const onChange = (lng: string) => {
      setLangState(normalizeLang(lng));
    };

    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  const setLang = (l: SupportedLang) => {
    setLangState(l);      // update context immediately
    i18n.changeLanguage(l); // tells i18next, which re-renders all `t()` calls
  };

  return (
    <LanguageCtx.Provider value={{ lang, setLang }}>
      {children}
    </LanguageCtx.Provider>
  );
}

export const useLanguage = () => useContext(LanguageCtx);
